'use client'

import { useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { useShopHandoff } from '@/features/auth/components/shop-link.client'
import { shopNextPath } from '@/shared/shop-site'
import { useSessionZipCode } from '@/shared/state/session-zip-provider'
import { leaveSite } from '@/shared/utilities/leave-site.utility'
import { addRecentSearch } from '../lib/recent-searches'
import { isExternalHref, searchIntentHref } from '../lib/search-navigation'
import type { SearchIntent } from '../models/search-intent.model'
import { MIN_SEARCH_QUERY_LENGTH } from '../search.constants'

/**
 * Opens the app a query belongs to with the query in that app's search box:
 * the Shop and ProFinder as full-page navigations — the Shop's through the
 * Handoff, so a signed-in visitor arrives signed in (ADR-0150) — the
 * directory, the hives list and the all-results page in-app.
 */
export function useSearchIntentNavigation() {
  const router = useRouter()
  const { sessionZip } = useSessionZipCode()
  const { openShop } = useShopHandoff()
  const zip = sessionZip.zipCode || undefined

  const goToQuery = useCallback(
    (query: string, intent: SearchIntent) => {
      const trimmed = query.trim()
      if (trimmed.length < MIN_SEARCH_QUERY_LENGTH) return
      addRecentSearch(trimmed)
      const href = searchIntentHref(intent, trimmed, zip)
      if (shopNextPath(href) !== null) void openShop(href)
      else if (isExternalHref(href)) leaveSite(href)
      else router.push(href)
    },
    [openShop, router, zip]
  )

  return { goToQuery }
}
